/**
 * PCO Log page (ADR 0002 §UI / PCO Log page).
 *
 * The bird's-eye view of every change order on the project. Replaces the
 * earlier `/pcos` thin filterable list — the new layout is built around
 * Joey's actual `25132 PCO Log.xlsx`:
 *
 *   ┌─ 6-up project totals strip ───────────────────────────────────┐
 *   │ Original │ Approved CO │ Revised │ Pending CO │ Pending Contr.│
 *   │ Submitted to date                                             │
 *   └────────────────────────────────────────────────────────────────┘
 *   ┌─ Filter chips ────────────────────────────────────────────────┐
 *   │ All / Open / Completed                [Add PCO] [Export]      │
 *   └────────────────────────────────────────────────────────────────┘
 *   ┌─ Slim PCO table ──────────────────────────────────────────────┐
 *   │ PCO │ Description │ Proposed │ Approved │ Submitted │ Remain. │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Four money columns, same order and meaning as the PCO Detail stat band,
 * shaped after Coffman's own log (Amount to date / Approved to date /
 * Progress). They replace the old single "Value" column, which showed
 * approved money or — dimmer — the proposal when nothing was approved:
 * one cell answering two questions, so neither was legible. Proposed and
 * Approved are now separate honest columns and the dim treatment is gone.
 *
 * There is no Status column. Funding state (Open / Proposed / Partially
 * funded / Funded) is exactly what the four money columns say, so the chip
 * was restating them. Void and Paid survive as a small tag beside the
 * description — those are lifecycle facts no dollar figure implies. A void
 * row blanks all four money cells, as it does on Coffman's sheet and in the
 * xlsx export. `derivePcoStatus` still drives the PCO Detail header chip and
 * the drawer pill; it just isn't read here.
 *
 * MOD/FWA references and comments have no column here — the drawer carries
 * all of that detail.
 *
 * Money-out is labeled "Submitted to date" on both the strip stat and the
 * table column: everything sent to Hoffman, whether Hoffman has approved it
 * yet or not. It previously read "Approved to date", which collided head-on
 * with Coffman's own PCO Log — there that exact phrase means funding Hoffman
 * has approved, the opposite direction of money — so the phrase is retired
 * from this UI. "Approved CO to date" on the strip is a different number and
 * stays: it's the funding-bucket/change-order ceiling, a byte-match to the
 * "Approved Change Orders To Date" label in Coffman's own spreadsheet.
 *
 * Row click → the PcoDrawer (right-hand peek: references, money picture,
 * submission ledger). The full PCO command-center page is one click deeper
 * via the drawer's "Open PCO →" action; the side rail remains the direct
 * route. The old two-level inline expansion retired in favor of the drawer.
 *
 * Layout: the page fills the viewport exactly (`.container--fill`) — the
 * chrome above is fixed and the table owns the ONLY vertical scrollbar.
 * No page scroll on top of table scroll.
 *
 * Client meeting 2026-07-22: most PCOs here are fixed-price change orders
 * hard-entered from Hoffman's own paperwork (not T&M rollups), so "+ New
 * PCO" is mounted directly on this page too (alongside the side rail's
 * global one) — see `newPcoOpen` below. Creating one here stays on the log:
 * the strip + table refetch and the new row opens in the drawer, instead of
 * navigating to the PCO's own page.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useSettingsActivityCodes, useSettingsPcos } from '../lib/useSettings';
import { useProjectTotals } from '../lib/usePcos';
import { ArrowRight, ChevronRight, Download, FolderOpen, Plus } from 'lucide-react';
import { useProjectSubmissions } from '../lib/useSubmissions';
import { EmptyState, PageHead, Skeleton } from '../components/design';
import { PcoDrawer } from '../components/PcoDrawer';
import { NewPcoModal } from '../components/NewPcoModal';
import { formatMoneyCents, formatPcoNumber } from '../lib/format';
import { proposedDisplayCents } from '../lib/pcoMoney';
import type { PcoStatus, SettingsPco, SubmissionSummary } from '@coffman-tm/shared';

// Filter chips collapse the 6-state pcos.status enum into 3 buckets that
// match Joey's mental model: All / Open (anything still in motion) /
// Completed (anything terminal). The full enum stays on the schema —
// just not surfaced as 8 individual chips. is_no_cost stays a row tag,
// no chip (zero no-cost PCOs in current data; bring it back later if
// Joey ever wants to filter for placeholders).
type StatusFilter = 'all' | 'open' | 'completed';

const OPEN_STATUSES: ReadonlyArray<PcoStatus> = [
  'open',
  'active',
  'pending_approval',
  'approved',
];
const COMPLETED_STATUSES: ReadonlyArray<PcoStatus> = ['paid', 'void'];

const FILTERS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'completed', label: 'Completed' },
];

export function PcoLogPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const pcos = useSettingsPcos();
  const totals = useProjectTotals();
  const codes = useSettingsActivityCodes();
  const projectSubs = useProjectSubmissions();
  const [filter, setFilter] = useState<StatusFilter>('all');
  // The row-peek drawer: clicking a log row opens this PCO's detail panel
  // (references, money picture, submission ledger) beside the table. The full
  // command-center page is one click deeper via the drawer's action.
  const [drawerPcoId, setDrawerPcoId] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);
  // "Add PCO" mounted directly on the log page too (client meeting
  // 2026-07-22 — most PCOs here are fixed-price change orders Joey needs to
  // hard-enter one after another). The rail's own button still exists for
  // "I'm elsewhere in the app and need to add one"; this one keeps the PM on
  // the log so the new row + its derived status are visible immediately
  // instead of navigating away to the PCO's own page. Its chooser also
  // carries the Historical Data branch, which is the log-side gesture: the
  // PCO Log is exactly the document being copied from.
  const [newPcoOpen, setNewPcoOpen] = useState(false);
  // The PCO the Add PCO flow just landed on. The drawer opening is the loud
  // signal; this is the quiet one — where in the table it went. Matters most
  // after Historical Data, which can add a dozen submissions to a row sitting
  // well below the fold.
  const [flashPcoId, setFlashPcoId] = useState<string | null>(null);

  // Every PCO's submissions (with member rollups) arrive in one prefetch when
  // the page mounts, so expanding a row is pure local state — no network wait,
  // no loading-row swap. Each expanded PCO renders its billing history grouped
  // by submission (PCO → submission → weekly rollups), mirroring the xlsx
  // export's grouping.
  const submissionsByPco = useMemo(() => {
    const m = new Map<string, SubmissionSummary[]>();
    for (const entry of projectSubs.data?.pcos ?? []) m.set(entry.pco_id, entry.submissions);
    return m;
  }, [projectSubs.data]);

  // Looked up from the full list (not `filtered`) so a filter change while
  // the drawer is open doesn't kill it.
  const drawerPco = drawerPcoId
    ? ((pcos.data ?? []).find((p) => p.id === drawerPcoId) ?? null)
    : null;

  const handleExportXlsx = async () => {
    if (!token) return;
    setExportBusy(true);
    setExportErr(null);
    try {
      const { blob, filename } = await api.pcos.downloadPcoLogXlsx(token);
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename ?? 'PCO_Log.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExportBusy(false);
    }
  };

  // Scroll the row the Add PCO flow just touched into view and flash it once
  // the refetched table has actually rendered it. Same treatment (and same
  // keyframes) as the Reconciliation deep-link.
  useEffect(() => {
    if (!flashPcoId) return;
    const el = document.getElementById(`pco-log-row-${flashPcoId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('row-flash');
    const t = window.setTimeout(() => {
      el.classList.remove('row-flash');
      setFlashPcoId(null);
    }, 2200);
    return () => window.clearTimeout(t);
  }, [flashPcoId, pcos.data]);

  const unmappedCodesCount = useMemo(
    // Unmapped AND not ignored — an ignored code needs no mapping (it never
    // bills), so counting it here would contradict Settings' own to-do list.
    () => (codes.data ?? []).filter((c) => c.mapped_pco === null && !c.ignored_at).length,
    [codes.data],
  );

  const filtered = useMemo<SettingsPco[]>(() => {
    return (pcos.data ?? [])
      .filter((p) => {
        if (filter === 'all') return true;
        if (filter === 'open') return (OPEN_STATUSES as readonly string[]).includes(p.status);
        if (filter === 'completed') return (COMPLETED_STATUSES as readonly string[]).includes(p.status);
        return true;
      })
      .sort((a, b) =>
        a.pco_number.localeCompare(b.pco_number, undefined, { numeric: true }),
      );
  }, [pcos.data, filter]);

  // Counts per filter chip. Open and Completed sum across their respective
  // status groups; All is the total.
  const counts = useMemo(() => {
    const out: Record<StatusFilter, number> = { all: 0, open: 0, completed: 0 };
    for (const p of pcos.data ?? []) {
      out.all += 1;
      if ((OPEN_STATUSES as readonly string[]).includes(p.status)) out.open += 1;
      if ((COMPLETED_STATUSES as readonly string[]).includes(p.status)) out.completed += 1;
    }
    return out;
  }, [pcos.data]);

  return (
    <div className="container container--fill">
      <PageHead
        title="PCO Log"
        sub={
          pcos.isLoading
            ? <Skeleton style={{ width: 220, height: 13 }} />
            : (pcos.data?.length ?? 0) === 0
              ? 'No PCOs on this project yet.'
              : (
                <>
                  Every change order on the project. Click a row for its
                  billing detail.
                  {unmappedCodesCount > 0 && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => navigate('/settings')}
                        style={{
                          color: 'var(--warning)',
                          fontWeight: 500,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {unmappedCodesCount} unmapped activity code
                        {unmappedCodesCount === 1 ? '' : 's'}
                        <ArrowRight size={14} aria-hidden />
                      </button>
                    </>
                  )}
                </>
              )
        }
      />

      {/* ===== Project totals strip — tight 6-up single row =====
          Previously two StatRows (4+2) at the design-system 24px stat
          size; that combo ate ~220px of vertical and pushed the actual
          PCO table below the fold. Compressed to a one-row 6-tile strip
          with smaller (16px) values + tighter padding so the strip
          comes in around 60-70px. Same data, less aggressive presence. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 1,
          background: 'var(--rule)',
          border: '1px solid var(--rule)',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        {(
          [
            ['Original contract', totals.data?.original_contract_cents],
            ['Approved CO to date', totals.data?.approved_co_to_date_cents],
            ['Revised contract', totals.data?.revised_contract_cents],
            ['Submitted to date', totals.data?.billed_to_date_cents],
            ['Pending CO to date', totals.data?.pending_co_to_date_cents],
            ['Pending contract', totals.data?.pending_contract_cents],
          ] as const
        ).map(([label, value]) => (
          <div
            key={label}
            style={{
              background: 'var(--surface)',
              padding: '8px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--ink-4)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {label}
            </div>
            <div
              style={{
                fontFamily: 'var(--sans)',
                fontSize: 16,
                fontWeight: 500,
                color: 'var(--ink)',
                letterSpacing: '-0.005em',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatMoneyCents(value ?? 0)}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12 }} />

      {/* Filter "tabs" + Export — pulled outside the white card so the
          control row reads like the Weekly Rollups/Totals/Sources sub-tabs
          on PCO Detail. Uses the design system's `.tabs` pill treatment. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 10,
        }}
      >
        <div className="tabs" role="tablist" aria-label="PCO Log filter">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={filter === f.value}
              className={filter === f.value ? 'active' : ''}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
              {counts[f.value] > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 11,
                    color: 'var(--ink-4)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {counts[f.value]}
                </span>
              )}
            </button>
          ))}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          {exportErr && (
            <span style={{ color: 'var(--danger)', fontSize: 12 }}>{exportErr}</span>
          )}
          {/* Outlined Export beside the solid primary Add PCO, in that order
              (Brandon 2026-08-05, from the reference mockup) — the pair reads
              as "the page's quiet action, then the page's main action". */}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleExportXlsx}
            disabled={exportBusy}
            style={{
              background: 'var(--surface)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            title="Download a byte-compatible replica of the 25132 PCO Log.xlsx"
          >
            <Download size={14} aria-hidden />
            {exportBusy ? 'Exporting…' : 'Export .xlsx'}
          </button>
          <button
            type="button"
            className="btn btn-cta"
            onClick={() => setNewPcoOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Plus size={14} aria-hidden />
            Add PCO
          </button>
        </div>
      </div>

      <div className="section section--card">
        {pcos.isLoading ? (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 88 }}>PCO</th>
                  <th style={{ width: '38%' }}>Description</th>
                  <th className="num money-sep" style={{ width: 130 }}>Proposed</th>
                  <th className="num" style={{ width: 130 }}>Approved</th>
                  <th className="num" style={{ width: 130 }}>Submitted</th>
                  <th className="num" style={{ width: 130 }}>Remaining</th>
                  <th style={{ width: 28 }} aria-hidden />
                </tr>
              </thead>
              <tbody aria-hidden>
                {Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td><Skeleton style={{ width: 56, height: 12 }} /></td>
                    <td><Skeleton style={{ width: '70%', height: 12 }} /></td>
                    <td className="num money-sep"><Skeleton style={{ width: 70, height: 12 }} /></td>
                    <td className="num"><Skeleton style={{ width: 70, height: 12 }} /></td>
                    <td className="num"><Skeleton style={{ width: 70, height: 12 }} /></td>
                    <td className="num"><Skeleton style={{ width: 70, height: 12 }} /></td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            eyebrow="PCO Log"
            icon={FolderOpen}
            text={
              (pcos.data?.length ?? 0) === 0
                ? 'No PCOs configured.'
                : filter === 'open'
                  ? 'No open PCOs — everything has been closed out.'
                  : filter === 'completed'
                    ? 'No completed PCOs yet.'
                    : 'No PCOs match this filter.'
            }
          />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                {/* Explicit widths drive the fixed table layout (see
                    .table-scroll .table). Four money columns in the same
                    order as the PCO Detail stat band, one-word headers —
                    the surrounding context and the help topics carry the
                    long forms. MOD/FWA live in the drawer (the reference
                    columns were mostly empty and wrapped rows ragged). */}
                <tr>
                  <th style={{ width: 88 }}>PCO</th>
                  {/* Capped, not auto: an auto description absorbs every spare
                      pixel on wide screens (~900px of it) and pushes the money
                      block to the far edge. 38% keeps the row readable with
                      four money columns; the leftover distributes across the
                      fixed columns. */}
                  <th style={{ width: '38%' }}>Description</th>
                  <th className="num money-sep" style={{ width: 130 }}>Proposed</th>
                  <th className="num" style={{ width: 130 }}>Approved</th>
                  <th className="num" style={{ width: 130 }}>Submitted</th>
                  <th className="num" style={{ width: 130 }}>Remaining</th>
                  <th style={{ width: 28 }} aria-hidden />
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => (
                  <PcoLogRow
                    key={p.id}
                    pco={p}
                    zebra={i % 2 === 1}
                    selected={drawerPcoId === p.id}
                    onOpen={() => setDrawerPcoId(p.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {drawerPco && (
        <PcoDrawer
          pco={drawerPco}
          submissions={submissionsByPco.get(drawerPco.id) ?? null}
          loading={projectSubs.isLoading}
          onClose={() => setDrawerPcoId(null)}
          onOpenPco={() => navigate(`/pcos/${drawerPco.id}`)}
          onOpenRollup={(weekEndingDate) => navigate(`/pcos/${drawerPco.id}/${weekEndingDate}`)}
        />
      )}

      <NewPcoModal
        open={newPcoOpen}
        onClose={() => setNewPcoOpen(false)}
        onCreated={async (newPco) => {
          setNewPcoOpen(false);
          // Stay on the log — refresh the strip + table so the new row and
          // its derived status (Proposed/Approved, from the price bucket(s)
          // just created) are already there, then peek it in the drawer for
          // a one-glance confirmation the price landed right. The submission
          // prefetch refreshes too: the Historical Data branch adds rows the
          // drawer's ledger is about to render.
          await Promise.all([pcos.refetch(), totals.refetch(), projectSubs.refetch()]);
          setDrawerPcoId(newPco.id);
          setFlashPcoId(newPco.id);
        }}
      />
    </div>
  );
}

function PcoLogRow({
  pco,
  zebra,
  selected,
  onOpen,
}: {
  pco: SettingsPco;
  zebra: boolean;
  /** This row's PCO is open in the drawer. */
  selected: boolean;
  onOpen: () => void;
}) {
  // A void PCO's money is withdrawn, not merely stalled: every money cell
  // blanks, matching Coffman's own log (void rows are cleared there) and the
  // xlsx export. The VOID tag beside the description is what's left of it.
  const isVoid = pco.status === 'void';
  const proposed = proposedDisplayCents(pco);
  const money = (cents: number | null) =>
    isVoid || cents === null || cents === 0 ? null : formatMoneyCents(cents);

  return (
    <tr
      id={`pco-log-row-${pco.id}`}
      className={[zebra ? 'row-alt' : '', selected ? 'row-selected' : ''].filter(Boolean).join(' ') || undefined}
      style={{ cursor: 'pointer' }}
      onClick={onOpen}
      aria-label={`Open detail for PCO ${formatPcoNumber(pco.pco_number)}`}
    >
      <td>
        <span className="mono" style={{ fontSize: 13 }}>
          {formatPcoNumber(pco.pco_number)}
        </span>
      </td>
      <td>
        {pco.description}
        {pco.is_no_cost && (
          <span
            className="muted"
            style={{ marginLeft: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            no cost
          </span>
        )}
        {/* The money columns tell the funding story on their own, so there's
            no status column any more. Void and Paid are the exception — they
            are lifecycle facts no dollar figure implies — so they ride along
            with the description as a tag. */}
        {(isVoid || pco.status === 'paid') && (
          <span
            className={`status-chip ${isVoid ? 's-void' : 's-paid'}`}
            style={{ marginLeft: 8, verticalAlign: 'middle' }}
          >
            {isVoid ? 'Void' : 'Paid'}
          </span>
        )}
      </td>
      <td className="num money-sep">{money(proposed)}</td>
      <td className="num">{money(pco.approved_total_cents)}</td>
      <td className="num">{money(pco.billed_to_date_cents)}</td>
      <td className="num">{money(pco.remaining_cents)}</td>
      {/* One more "this row opens" cue beside the hover state (Brandon
          2026-08-05) — the chevron every drill-down list in the app uses. */}
      <td style={{ width: 28, paddingRight: 4 }}>
        <ChevronRight size={14} aria-hidden style={{ color: 'var(--ink-4)', display: 'block' }} />
      </td>
    </tr>
  );
}
